/**
 * Model-sizing callout overlay for PDP media gallery.
 *
 * Reads the already-parsed Fit Suggestion block
 * ([data-section-content="fit-suggestion"]) populated by
 * product-description-parser.js, finds the sentence that mentions
 * "{Name} is {height}... wearing a size {size}", and overlays a
 * condensed pill caption at the bottom-left of every image slide
 * in the MAIN product gallery (never the thumbs).
 *
 * Defensive choices:
 *  - Scoped to swiper-product-gallery only (ignores swiper-product-thumbs).
 *  - Inline-sized pill (width: max-content) so no flex/grid parent can
 *    stretch it into a full-image blob.
 *  - applyCallouts is idempotent AND disconnects the MutationObserver
 *    around its own DOM writes, so we never trigger an observer loop.
 */
(function () {
  'use strict';

  var STYLE_ID = 'product-model-callout-styles';
  var CALLOUT_CLASS = 'product__media-model-callout';
  var CALLOUT_ATTR = 'data-model-callout-text';

  // Set to true in the browser console to log what's happening:
  //   window.__modelCalloutDebug = true;
  function log() {
    if (window.__modelCalloutDebug) {
      try { console.log.apply(console, ['[model-callout]'].concat([].slice.call(arguments))); } catch (e) {}
    }
  }

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = [
      // Only the main gallery's slide containers get position:relative — NOT thumbs.
      'swiper-product-gallery .swiper-slide,',
      '.main-product__media--grid-item { position: relative; }',
      '.' + CALLOUT_CLASS + ' {',
      '  position: absolute !important;',
      '  left: 12px !important;',
      '  bottom: 12px !important;',
      '  right: auto !important;',
      '  top: auto !important;',
      '  z-index: 5 !important;',
      '  display: inline-flex !important;',
      '  align-items: center !important;',
      '  width: max-content !important;',
      '  max-width: calc(100% - 24px) !important;',
      '  height: auto !important;',
      '  min-width: 0 !important;',
      '  min-height: 0 !important;',
      '  flex: 0 0 auto !important;',
      '  box-sizing: border-box !important;',
      '  pointer-events: none !important;',
      '  background: rgba(255, 255, 255, 0.92) !important;',
      '  color: #111 !important;',
      '  font-family: inherit !important;',
      '  font-size: 12px !important;',
      '  line-height: 1.3 !important;',
      '  font-weight: 500 !important;',
      '  padding: 8px 14px !important;',
      '  border-radius: 20px !important;',
      '  letter-spacing: 0.01em !important;',
      '  white-space: nowrap !important;',
      '  overflow: hidden !important;',
      '  text-overflow: ellipsis !important;',
      '  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08) !important;',
      '  backdrop-filter: blur(6px);',
      '  -webkit-backdrop-filter: blur(6px);',
      '  will-change: opacity, transform;',
      '}',
      // Initial pre-animation state — JS removes this once the enter animation completes.
      '.' + CALLOUT_CLASS + '[data-animating="enter"] {',
      '  opacity: 0 !important;',
      '  transform: translate3d(0, 12px, 0) scale(0.96) !important;',
      '}',
      // Mobile: only show the callout on the first image (marked in JS).
      '@media (max-width: 749px) {',
      '  .' + CALLOUT_CLASS + ' {',
      '    left: 10px !important;',
      '    bottom: 10px !important;',
      '    font-size: 11px !important;',
      '    padding: 6px 12px !important;',
      '  }',
      '  .' + CALLOUT_CLASS + ':not([data-first-image="true"]) {',
      '    display: none !important;',
      '  }',
      '}',
    ].join('\n');
    document.head.appendChild(style);
  }

  /**
   * Extracts a condensed model-sizing string from the Fit Suggestion block.
   * Matches "{Name} is {height}...(wearing|wears) a size {size}" and strips
   * metric equivalents and trailing measurements.
   *
   * If more than one model is listed (e.g. a hero + a curve model), we
   * suppress the callout entirely — the photo overlay is meant for a single
   * model, and showing only one when the description lists two would be
   * misleading.
   *
   * Returns e.g. "Devyn is 5'5" wearing a size S", or null if zero or 2+ matches.
   */
  function extractModelCallout(fitSuggestionEl) {
    if (!fitSuggestionEl) return null;

    var candidates = fitSuggestionEl.querySelectorAll('p, li');
    var PRIMARY = /^([A-Z][\w\-\s]*?)\s+is\s+([^,]+?)(?:,\s*|\s+)(?:wearing|wears)\s+a\s+size\s+([A-Z0-9\-\/]+)/i;
    // Looser pattern used only to count model lines — catches the "wears a size"
    // phrasing even when the sentence doesn't lead with "{Name} is".
    var WEARS = /(wearing|wears)\s+a\s+size/i;

    var firstMatch = null;
    var matchCount = 0;

    for (var i = 0; i < candidates.length; i++) {
      var text = (candidates[i].textContent || '').replace(/\s+/g, ' ').trim();
      if (!text || !WEARS.test(text)) continue;

      matchCount++;
      if (matchCount > 1) {
        log('multiple model fittings detected — suppressing callout');
        return null;
      }

      var match = text.match(PRIMARY);
      if (match) {
        var name = match[1].trim();
        var height = match[2].trim().split('/')[0].trim().split(',')[0].trim();
        var size = match[3].trim();
        firstMatch = name + ' is ' + height + ' wearing a size ' + size;
      } else {
        // Loose-match fallback — keep the sentence verbatim
        firstMatch = text;
      }
    }

    return firstMatch;
  }

  function isImageSlide(slideEl) {
    var mediaType = slideEl.getAttribute && slideEl.getAttribute('data-media-type');
    if (mediaType && mediaType !== 'image') return false;
    if (slideEl.querySelector('video, iframe')) return false;
    if (slideEl.querySelector('model-viewer, .model-viewer-container')) return false;
    if (!slideEl.querySelector('img')) return false;
    return true;
  }

  // The ONE place slides are located. Scoped to the main gallery, never the thumbs.
  //   - swiper-product-gallery .swiper-slide: the slider layout (mobile + desktop)
  //   - .main-product__media--grid-item: the desktop grid layout
  function findMainGallerySlides() {
    var main = document.querySelector('.main-product__media');
    if (!main) return [];
    var sliderSlides = main.querySelectorAll('swiper-product-gallery .swiper-slide');
    var gridItems = main.querySelectorAll('.main-product__media--grid-item');
    // Merge into a single array so our observer / loop has one list to walk.
    var out = [];
    for (var i = 0; i < sliderSlides.length; i++) out.push(sliderSlides[i]);
    for (var j = 0; j < gridItems.length; j++) out.push(gridItems[j]);
    return out;
  }

  var observer = null;
  var mutating = false;

  function applyCallouts() {
    if (mutating) return; // extra safety
    var fitSuggestionEl = document.querySelector('[data-section-content="fit-suggestion"]');
    var calloutText = extractModelCallout(fitSuggestionEl);
    log('applyCallouts, calloutText =', calloutText);

    var slides = findMainGallerySlides();
    if (!slides.length) return;

    // Track which slide holds the first image so we can tag it for mobile visibility.
    var firstImageMarked = false;

    mutating = true;
    if (observer) observer.disconnect();
    try {
      slides.forEach(function (slide) {
        var existing = slide.querySelector(':scope > .' + CALLOUT_CLASS);

        if (!calloutText || !isImageSlide(slide)) {
          if (existing) existing.parentNode.removeChild(existing);
          return;
        }

        var isFirst = !firstImageMarked;
        firstImageMarked = true;

        if (existing) {
          // Keep the first-image flag in sync even if we're skipping a text update.
          if (isFirst) {
            existing.setAttribute('data-first-image', 'true');
          } else {
            existing.removeAttribute('data-first-image');
          }
          if (existing.getAttribute(CALLOUT_ATTR) === calloutText) return; // text unchanged — no-op
          existing.setAttribute(CALLOUT_ATTR, calloutText);
          existing.textContent = calloutText;
          return;
        }

        var pill = document.createElement('div');
        pill.className = CALLOUT_CLASS;
        pill.setAttribute(CALLOUT_ATTR, calloutText);
        if (isFirst) pill.setAttribute('data-first-image', 'true');
        pill.textContent = calloutText;
        // Hide pre-animation so we don't flash at final position for one frame.
        pill.setAttribute('data-animating', 'enter');
        slide.appendChild(pill);
        playEnterAnimation(pill);
      });
    } finally {
      mutating = false;
      if (observer) startObserving();
    }
  }

  /**
   * Plays a subtle rise + fade + scale-in on a freshly appended pill, via the
   * Web Animations API (bypasses any CSS cascade issues). Respects OS-level
   * reduced-motion preference. Removes the data-animating attribute on end so
   * the pill sits at its resting state going forward.
   */
  function playEnterAnimation(pill) {
    var prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var finalize = function () { pill.removeAttribute('data-animating'); };

    if (prefersReduced || typeof pill.animate !== 'function') {
      finalize();
      return;
    }

    // Ensure the pre-animation state is painted before we animate off it.
    // (Force a reflow so the transition from hidden → visible is guaranteed.)
    // eslint-disable-next-line no-unused-expressions
    pill.offsetWidth;

    var anim = pill.animate(
      [
        { opacity: 0, transform: 'translate3d(0, 12px, 0) scale(0.96)' },
        { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
      ],
      {
        duration: 520,
        delay: 140,
        easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
        fill: 'both',
      }
    );
    anim.onfinish = finalize;
    // Safety net in case onfinish doesn't fire (e.g. tab backgrounded).
    setTimeout(finalize, 900);
  }

  function startObserving() {
    if (!observer) return;
    var mainSlider = document.querySelector('swiper-product-gallery');
    var gridContainer = document.querySelector('.main-product__media--grid');
    if (mainSlider) observer.observe(mainSlider, { childList: true, subtree: true });
    if (gridContainer) observer.observe(gridContainer, { childList: true, subtree: true });
  }

  function init() {
    injectStyles();
    applyCallouts();
    // The Fit Suggestion DOM is populated by product-description-parser.js
    // which also runs on DOMContentLoaded — re-run a couple of times to cover
    // the race where the parser finishes after us.
    setTimeout(applyCallouts, 200);
    setTimeout(applyCallouts, 600);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-render on variant swap (color swatch change).
  document.addEventListener('product-info:loaded', function () {
    setTimeout(applyCallouts, 150);
    setTimeout(applyCallouts, 500);
  });

  // Catch late-arriving slides. Observer only watches the main gallery subtree,
  // not the thumbs strip, and we disconnect it during our own writes so we can't
  // trigger ourselves in a loop.
  if (typeof MutationObserver !== 'undefined') {
    observer = new MutationObserver(function () {
      if (mutating) return;
      clearTimeout(observer._debounce);
      observer._debounce = setTimeout(applyCallouts, 150);
    });
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', startObserving);
    } else {
      startObserving();
    }
  }
})();
